/**
 * @ken/module-document-intake — frontend entry point (slice A, W8-04-A-T3).
 *
 * The route table only: the screen arrives through lazyScreen (ADR-040), so
 * nothing of the document list is in the first download.
 */

import { lazyScreen, type AnyRoute, type CreateModuleRoutes } from "@ken/cockpit-types";
import { createRoute } from "@tanstack/react-router";

const DocumentsPage = lazyScreen(() => import("./routes/documents"), "DocumentsPage");
// Slice B (W8-04-B-T2): the desk document page, `?page=<n>` opens on page n.
const DocumentPage = lazyScreen(() => import("./routes/document"), "DocumentPage");
// Slice G (W8-04-G-T5): the document types wizard, administrators only; lazy like the rest.
const TypesPage = lazyScreen(() => import("./routes/types"), "TypesPage");
const TypePage = lazyScreen(() => import("./routes/typePage"), "TypePage");
// Slice G (W8-04-G-T6): the New document type flow; `?from=<type key>` starts it with that
// type's "Ken cannot read these" documents picked.
const NewTypePage = lazyScreen(() => import("./routes/newType"), "NewTypePage");
const TYPE_TABS = ["instructions", "notes", "recognition", "names", "samples"];

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const documentsRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "documents",
    component: DocumentsPage,
  });
  const documentRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "documents/$documentId",
    validateSearch: (raw: Record<string, unknown>): { page?: number; q?: string } => {
      const page = Number(raw["page"]);
      return {
        ...(Number.isFinite(page) && page >= 1 ? { page: Math.floor(page) } : {}),
        ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      };
    },
    component: DocumentPage,
  });
  const typesRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "settings/document_intake/types",
    component: TypesPage,
  });
  const typeRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "settings/document_intake/types/$typeKey",
    validateSearch: (raw: Record<string, unknown>): { tab?: string } =>
      typeof raw["tab"] === "string" && TYPE_TABS.includes(raw["tab"]) ? { tab: raw["tab"] } : {},
    component: TypePage,
  });
  const newTypeRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "settings/document_intake/new-type",
    validateSearch: (raw: Record<string, unknown>): { from?: string } =>
      typeof raw["from"] === "string" && raw["from"] ? { from: raw["from"] } : {},
    component: NewTypePage,
  });
  return [documentsRoute, documentRoute, typesRoute, typeRoute, newTypeRoute];
};
